/* Generic ticketed-event booking page. Used by Australia Day (day + night) and Boxing Day.
   The event_key comes from `?event=...` query string. Each event has its own theme block
   (THEMES) so colours adapt per event. NYE has its own page, this is for $145/$110 events.
*/
import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ShieldCheck, Sparkles, Check, Calendar, Clock, MapPin, Users, Crown, AlertCircle, Ticket } from "lucide-react";
import SEOHead from "../components/SEOHead";
import useSurcharge from "../hooks/useSurcharge";
import Starfield from "../components/Starfield";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ──────────────────────── per-event themes ────────────────────────
const THEMES = {
 "aus-day-2026-day": {
  accent: "#E8B53A", hot: "#FFE08A", dark1: "#0E1B1F", dark2: "#16292F", dark3: "#0A1417",
  glow: "rgba(255,200,90,0.6)",
  starColor: "#FFE08A", starGlow: "rgba(255,200,90,0.6)",
  fontSerif: "'Playfair Display', serif",
  title: "Australia Day Cruise",
  subtitle: "Day session · 26 January 2027",
  blurb: "4 hours on Sydney Harbour with canapés, mini buffet and unlimited soft drinks. Boarding King St Wharf 6 10:45am. Beer, wine and spirits available to purchase at the licensed bar.",
  poster: "https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/zd1xz4zn_AUSTRALIA-DAY-CRUISES-SYDNEY-HARBOUR.jpg",
  parentSlug: "australia-day-cruises-sydney-harbour",
  inclusions: [
   "4 hour cruise on Magic", "Canapés on arrival + standing mini buffet (included)", "Unlimited soft drinks (included)", "Front-row Sydney Harbour Regatta + Ferrython", "Beer, wine, spirits · available to purchase at the licensed bar (extra)"],
 },"aus-day-2026-night": {
  accent: "#E8B53A", hot: "#FFCC70", dark1: "#0A0414", dark2: "#1A0824", dark3: "#0A0212",
  glow: "rgba(255,200,90,0.6)",
  starColor: "#FFE08A", starGlow: "rgba(255,200,90,0.6)",
  fontSerif: "'Playfair Display', serif",
  title: "Australia Day Cruise",
  subtitle: "Night session · fireworks · 26 January 2027",
  blurb: "Watch the Australia Day fireworks light up Sydney Harbour. Canapés + mini buffet + unlimited soft drinks included. Boarding King St Wharf 6 6:30pm, returning ~10:30pm. Beer, wine and spirits available to purchase at the licensed bar.",
  poster: "https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/zd1xz4zn_AUSTRALIA-DAY-CRUISES-SYDNEY-HARBOUR.jpg",
  parentSlug: "australia-day-cruises-sydney-harbour",
  inclusions: [
   "4 hour cruise on Magic", "Canapés on arrival + standing mini buffet (included)", "Unlimited soft drinks (included)", "Australia Day fireworks · prime viewing", "Beer, wine, spirits · available to purchase at the licensed bar (extra)"],
 },"boxing-day-2026": {
  accent: "#DC2626", hot: "#F87171", dark1: "#0A1428", dark2: "#0E1B36", dark3: "#08111F",
  glow: "rgba(248,113,113,0.55)",
  starColor: "#FCA5A5", starGlow: "rgba(252,165,165,0.5)",
  fontSerif: "'Playfair Display', serif",
  title: "Boxing Day Cruise",
  subtitle: "Sydney to Hobart · 26 December 2026",
  blurb: "Front-row view of the Rolex Sydney to Hobart yacht race start at 1pm. Boarding King St Wharf 6 11:45am. BYO picnic basket welcome.",
  poster: "https://customer-assets.emergentagent.com/job_magic-harbour-party/artifacts/mm8n8s47_boxing-day-cruises-sydney-harbour-magic.jpg",
  parentSlug: "boxing-day-cruises-sydney",
  inclusions: [
   "Prime position for the Sydney to Hobart race start (1pm)", "Unlimited soft drinks (included)", "BYO picnic basket welcome", "Bar snacks · available to purchase (extra)", "Beer, wine, spirits & cocktails · available to purchase at the bar (extra)", "No BYO alcohol permitted"],
 },
};

export default function BookEvent() {
 const [params] = useSearchParams();
 const navigate = useNavigate();
 const eventKey = params.get("event") || "aus-day-2026-day";
 const theme = THEMES[eventKey] || THEMES["aus-day-2026-day"];

 const [evt, setEvt] = useState(null);
 const [loading, setLoading] = useState(true);
 const [step, setStep] = useState(1);
 const [picked, setPicked] = useState({ adult_qty: 2, child_qty: 0 });
 const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
 const [submitting, setSubmitting] = useState(false);

 useEffect(() => { document.title = `Buy ${theme.title} Tickets | Magic Cruises`; }, [theme.title]);
 useEffect(() => {
  if (params.get("cancelled")) toast.info("Payment cancelled, your seats were not booked.");
 }, [params]);

 useEffect(() => {
  if (!THEMES[eventKey]) { navigate("/cruises/australia-day-cruises-sydney-harbour"); return; }
  (async () => {
   try {
    const r = await axios.get(`${API}/events/${eventKey}/availability`);
    setEvt(r.data);
   } catch (e) { toast.error("Could not load live availability."); }
   finally { setLoading(false); }
  })();
 }, [eventKey, navigate]);

 const totalQty = picked.adult_qty + picked.child_qty;
 const adultPrice = evt?.pricing?.adult ?? 0;
 const childPrice = evt?.pricing?.child ?? 0;
 const subtotal = (picked.adult_qty * adultPrice) + (picked.child_qty * childPrice);
 const { cfg: surchargeCfg, apply: applySurcharge } = useSurcharge();
 const { surcharge, total } = applySurcharge(subtotal);
 const remaining = evt?.remaining ?? 120;
 const soldOut = evt?.status === "sold_out";

 const canStep1 = totalQty > 0 && totalQty <= remaining;
 const canStep2 = customer.name.trim().length >= 2 && /.+@.+\..+/.test(customer.email);

 const startCheckout = async () => {
  if (!canStep2) { toast.error("Please complete your name and email"); return; }
  setSubmitting(true);
  try {
   const r = await axios.post(`${API}/checkout/event`, {
    event_key: eventKey,
    adult_qty: picked.adult_qty, child_qty: picked.child_qty,
    customer_name: customer.name, customer_email: customer.email, customer_phone: customer.phone,
    origin_url: window.location.origin,
   });
   window.location.href = r.data.url;
  } catch (e) {
   toast.error(e?.response?.data?.detail || "Checkout failed. Please try again.");
   setSubmitting(false);
  }
 };

 const StepDot = ({ n, label }) => (
  <div className="flex items-center gap-2.5 shrink-0">
   <span className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-black ${step === n ? "text-[#0A0A0A] shadow-lg" : step > n ? "bg-white/15" : "bg-white/8 text-white/50"}`} style={step === n ? { backgroundColor: theme.accent, boxShadow: `0 8px 24px -4px ${theme.glow}` } : step > n ? { color: theme.accent } : {}}>{step > n ? <Check className="w-4 h-4" strokeWidth={3} /> : n}</span>
   <span className={`text-xs uppercase tracking-[0.22em] font-bold hidden sm:inline ${step === n ? "text-white" : "text-white/60"}`}>{label}</span>
  </div>
 );

 return (
  <main className="text-white min-h-screen relative overflow-hidden" style={{ backgroundColor: theme.dark3 }} data-testid={`event-book-${eventKey}`}>
   <SEOHead title={`Buy ${theme.title} Tickets, ${theme.subtitle} | Magic Cruises`} description={theme.blurb} />

   {/* Atmospheric bg */}
   <div className="fixed inset-0 pointer-events-none">
    <div className="absolute top-0 left-1/3 w-[40rem] h-[40rem] rounded-full blur-3xl animate-pulse" style={{ backgroundColor: `${theme.accent}1A` }} />
    <div className="absolute bottom-0 right-1/4 w-[36rem] h-[36rem] rounded-full blur-3xl animate-pulse" style={{ animationDelay: "2s", backgroundColor: `${theme.hot}14` }} />
   </div>
   <Starfield density={55} color={theme.starColor} glow={theme.starGlow} fixed={true} intensity={0.55} />

   <div className="relative pt-28 pb-32 px-5 lg:px-10 mx-auto max-w-6xl">
    <Link to={`/cruises/${theme.parentSlug}`} className="inline-flex items-center gap-2 text-white/65 hover:text-white text-xs font-bold uppercase tracking-[0.28em] mb-7 transition-colors" style={{ "--hover": theme.accent }} data-testid="event-book-back-link">
     <ArrowLeft className="w-3.5 h-3.5" /> Back to {theme.title}
    </Link>

    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] font-bold mb-4" style={{ color: theme.accent }}>
     <Ticket className="w-3.5 h-3.5" /> {theme.subtitle}
    </div>
    <h1 className="font-black tracking-tight leading-[0.95] mb-2" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)", fontFamily: theme.fontSerif }}>
     Buy your <em className="font-light italic" style={{ color: theme.accent }}>{theme.title.toLowerCase()} ticket.</em>
    </h1>
    <p className="text-white/72 text-base lg:text-lg mt-3 mb-10 max-w-2xl leading-[1.7]">{theme.blurb}</p>

    <div className="flex items-center gap-3 sm:gap-5 overflow-x-auto pb-3 mb-10 -mx-2 px-2" data-testid="event-book-steps">
     <StepDot n={1} label="Tickets" />
     <div className="h-px w-6 sm:w-10 bg-white/15 shrink-0" />
     <StepDot n={2} label="Your details" />
     <div className="h-px w-6 sm:w-10 bg-white/15 shrink-0" />
     <StepDot n={3} label="Pay" />
    </div>

    {loading && <div className="text-white/65 text-center py-20">Loading live availability…</div>}

    {!loading && soldOut && (
     <div className="rounded-3xl ring-1 ring-red-400/30 bg-red-500/10 p-8 lg:p-10 text-center" data-testid="event-sold-out">
      <AlertCircle className="w-10 h-10 text-red-300 mx-auto" />
      <h2 className="font-black text-3xl text-white mt-4">{theme.title} is sold out.</h2>
      <p className="text-white/75 text-base mt-3 max-w-lg mx-auto">Drop your details and we'll prioritise you for the next release.</p>
      <Link to={`/cruises/${theme.parentSlug}#enquiry`} className="inline-flex items-center gap-2 mt-6 px-7 py-3.5 rounded-full text-[#0A0A0A] font-black text-xs uppercase tracking-[0.22em]" style={{ backgroundColor: theme.accent }}>
       Join the waitlist <ArrowRight className="w-4 h-4" />
      </Link>
     </div>
    )}

    {!loading && !soldOut && evt && (
     <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
      <div className="lg:col-span-8">
       {step === 1 && (
        <div data-testid="event-step-tickets">
         <h2 className="text-3xl md:text-4xl font-black mb-3">1. How many tickets?</h2>
         <p className="text-white/82 text-base mb-8 leading-[1.7]">Up to 20 tickets per booking. For groups of 10+ please <Link to={`/cruises/${theme.parentSlug}#enquiry`} className="underline" style={{ color: theme.accent }}>enquire about group bookings</Link> (deposit option available).</p>
         <div className="space-y-4">
          {[
           { k: "adult_qty", label: "Adult", price: adultPrice, hint: "13+ years · all inclusive" },
           { k: "child_qty", label: "Child", price: childPrice, hint: "12 & under · all inclusive" }].map(row => (
           <div key={row.k} className="flex items-center justify-between gap-4 p-6 rounded-2xl ring-1 ring-white/10 bg-white/[0.04]" data-testid={`event-row-${row.k}`}>
            <div>
             <div className="text-white text-2xl font-black">{row.label}</div>
             <div className="text-white/70 text-sm uppercase tracking-widest font-bold mt-1.5">{row.hint} · ${row.price.toLocaleString()} ea</div>
            </div>
            <div className="flex items-center gap-4">
             <button onClick={() => setPicked(p => ({ ...p, [row.k]: Math.max(0, p[row.k] - 1) }))} className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white text-2xl font-black transition-colors" data-testid={`event-qty-${row.k}-minus`}>−</button>
             <span className="w-12 text-center text-white text-3xl font-black tabular-nums" data-testid={`event-qty-${row.k}`}>{picked[row.k]}</span>
             <button onClick={() => setPicked(p => ({ ...p, [row.k]: Math.min(20, p[row.k] + 1) }))} className="w-12 h-12 rounded-full text-[#0A0A0A] text-2xl font-black transition-colors" style={{ backgroundColor: theme.accent }} data-testid={`event-qty-${row.k}-plus`}>+</button>
            </div>
           </div>
          ))}
          {totalQty > remaining && (
           <div className="flex items-start gap-3 p-4 rounded-xl bg-red-500/10 border border-red-400/30 text-red-200 text-base" data-testid="event-qty-error">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" /><div>Only {remaining} seats remain. Reduce your quantity to continue.</div>
           </div>
          )}
          {remaining > 0 && remaining <= 30 && (
           <div className="flex items-start gap-3 p-4 rounded-xl border text-base font-bold" style={{ backgroundColor: `${theme.accent}1A`, borderColor: `${theme.accent}55`, color: theme.hot }} data-testid="event-low-warning">
            <Sparkles className="w-5 h-5 shrink-0 mt-0.5" /><div>Only {remaining} tickets left. Selling fast.</div>
           </div>
          )}
         </div>
        </div>
       )}

       {step === 2 && (
        <div data-testid="event-step-customer">
         <h2 className="text-3xl md:text-4xl font-black mb-3">2. Your details</h2>
         <p className="text-white/82 text-base mb-8 leading-[1.7]">We'll email your tickets and boarding pass to this address.</p>
         <div className="grid sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2"><label className="text-white/70 text-xs uppercase tracking-widest font-bold">Full name *</label><input data-testid="event-cust-name" value={customer.name} onChange={e => setCustomer(c => ({ ...c, name: e.target.value }))} className="w-full mt-2 px-5 py-4 rounded-xl bg-white/5 border border-white/15 focus:bg-white/10 outline-none text-white text-lg" style={{ "--tw-border-opacity": 1 }} onFocus={e => e.target.style.borderColor = theme.accent} onBlur={e => e.target.style.borderColor = ""} placeholder="Your full name" /></div>
          <div><label className="text-white/70 text-xs uppercase tracking-widest font-bold">Email *</label><input data-testid="event-cust-email" type="email" value={customer.email} onChange={e => setCustomer(c => ({ ...c, email: e.target.value }))} className="w-full mt-2 px-5 py-4 rounded-xl bg-white/5 border border-white/15 focus:bg-white/10 outline-none text-white text-lg" onFocus={e => e.target.style.borderColor = theme.accent} onBlur={e => e.target.style.borderColor = ""} placeholder="you@email.com" /></div>
          <div><label className="text-white/70 text-xs uppercase tracking-widest font-bold">Phone</label><input data-testid="event-cust-phone" value={customer.phone} onChange={e => setCustomer(c => ({ ...c, phone: e.target.value }))} className="w-full mt-2 px-5 py-4 rounded-xl bg-white/5 border border-white/15 focus:bg-white/10 outline-none text-white text-lg" onFocus={e => e.target.style.borderColor = theme.accent} onBlur={e => e.target.style.borderColor = ""} placeholder="0400…" /></div>
         </div>
         <div className="flex items-center gap-2 mt-8 text-white/82 text-sm">
          <ShieldCheck className="w-4 h-4" style={{ color: theme.accent }} /> Secure payment via Stripe · Cards in AUD · Tickets emailed instantly after payment
         </div>
        </div>
       )}

       <div className="flex justify-between gap-4 mt-12 pb-24 lg:pb-0">
        <button disabled={step === 1} onClick={() => setStep(s => Math.max(1, s - 1))} className="px-7 py-5 rounded-full bg-white/5 hover:bg-white/10 text-white text-sm font-black uppercase tracking-[0.22em] disabled:opacity-30 disabled:cursor-not-allowed" data-testid="event-step-prev">
         <ArrowLeft className="w-4 h-4 inline mr-2" /> Back
        </button>
        {step < 2 ? (
         <button disabled={!canStep1} onClick={() => setStep(2)} className="px-10 py-5 rounded-full text-[#0A0A0A] text-sm font-black uppercase tracking-[0.22em] disabled:opacity-40 disabled:cursor-not-allowed transition-all" style={{ background: `linear-gradient(135deg, ${theme.hot} 0%, ${theme.accent} 100%)`, boxShadow: `0 15px 40px -10px ${theme.glow}` }} data-testid="event-step-next">
          Continue <ArrowRight className="w-4 h-4 inline ml-2" />
         </button>
        ) : (
         <button disabled={!canStep2 || submitting} onClick={startCheckout} className="px-10 py-5 rounded-full text-[#0A0A0A] text-sm font-black uppercase tracking-[0.22em] disabled:opacity-40 disabled:cursor-not-allowed transition-all" style={{ background: `linear-gradient(135deg, ${theme.hot} 0%, ${theme.accent} 100%)`, boxShadow: `0 15px 40px -10px ${theme.glow}` }} data-testid="event-step-checkout">
          {submitting ? "Redirecting to Stripe…" : <>Pay ${total.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} AUD <Sparkles className="w-4 h-4 inline ml-2" /></>}
         </button>
        )}
       </div>
      </div>

      <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start" data-testid="event-book-summary">
       <div className="rounded-3xl ring-1 p-7 lg:p-8 backdrop-blur-md" style={{ background: `linear-gradient(135deg, ${theme.accent}1A, ${theme.dark2}cc 60%, ${theme.dark3}cc)`, boxShadow: `0 30px 80px -20px ${theme.glow}`, borderColor: `${theme.accent}33` }}>
        <div className="text-[11px] uppercase tracking-[0.32em] font-bold mb-5" style={{ color: theme.accent }}>Your cruise</div>
        <div className="text-white text-xl font-black mb-1">{evt.name}</div>
        <div className="text-white/65 text-xs uppercase tracking-widest font-bold mb-5">{theme.subtitle}</div>
        <dl className="space-y-4 text-base">
         <Row Icon={Calendar} label="Date" value={new Date(evt.date).toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} accent={theme.accent} />
         <Row Icon={Clock} label="Boarding" value={evt.boarding} accent={theme.accent} />
         <Row Icon={Clock} label="Returns" value={`~${evt.return}`} accent={theme.accent} />
         <Row Icon={MapPin} label="Wharf" value={evt.wharf} accent={theme.accent} />
         <Row Icon={Users} label="Tickets" value={totalQty > 0 ? `${picked.adult_qty} adult · ${picked.child_qty} child` : ""} accent={theme.accent} />
        </dl>
        <div className="border-t border-white/15 mt-6 pt-6 space-y-3">
         <div className="flex justify-between items-baseline text-white/85 text-base">
          <span><span className="font-black text-white">{picked.adult_qty}</span> × Adult <span className="text-white/50 text-sm">(${adultPrice.toLocaleString()} ea)</span></span>
          <span className="text-lg font-bold tabular-nums">${(picked.adult_qty * adultPrice).toLocaleString()}</span>
         </div>
         <div className="flex justify-between items-baseline text-white/85 text-base">
          <span><span className="font-black text-white">{picked.child_qty}</span> × Child <span className="text-white/50 text-sm">(${childPrice.toLocaleString()} ea)</span></span>
          <span className="text-lg font-bold tabular-nums">${(picked.child_qty * childPrice).toLocaleString()}</span>
         </div>
         {surchargeCfg.enabled && surcharge > 0 && (
          <div className="flex justify-between items-baseline pt-3 mt-2 border-t border-white/10 text-white/75 text-sm">
           <span>{surchargeCfg.label}</span>
           <span className="font-bold tabular-nums">+${surcharge.toFixed(2)}</span>
          </div>
         )}
         <div className="flex justify-between items-baseline pt-4 mt-3 border-t border-white/15">
          <span className="text-white text-2xl font-black">Total AUD</span>
          <span className="text-4xl font-black tabular-nums bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, ${theme.hot} 0%, ${theme.accent} 100%)` }}>${total.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span>
         </div>
         <div className="text-white/55 text-[11px] uppercase tracking-widest font-bold pt-1">GST inclusive · Free cancellation up to 72hrs prior</div>
        </div>
        <ul className="mt-7 space-y-2.5 text-white/82 text-sm">
         {theme.inclusions.map((inc, i) => (
          <li key={i} className="flex items-start gap-2.5">
           {i === 0 ? <Crown className="w-4 h-4 shrink-0 mt-0.5" style={{ color: theme.accent }} /> : <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: theme.accent }} />}
           {inc}
          </li>
         ))}
        </ul>
       </div>
      </aside>
     </div>
    )}
   </div>

   {/* Sticky bottom CTA bar , always in view */}
   <div className="fixed bottom-0 inset-x-0 z-40 backdrop-blur-xl border-t shadow-[0_-15px_45px_-10px_rgba(0,0,0,0.7)]" style={{ background: `${theme.dark3 || "hsl(220,55%,8%)"}f0`, borderColor: `${theme.accent}44` }} data-testid="sticky-cta-bar">
    <div className="mx-auto max-w-6xl px-4 py-3 flex items-center gap-3">
     <div className="hidden sm:block flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: theme.accent }}>Step {step} of 2{step === 2 ? " · Final" : ""}</div>
      <div className="text-white text-sm font-bold truncate">
       {step === 1 && `Choose your ${evt?.name || theme.title} tickets`}
       {step === 2 && `Pay $${total.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} AUD · ${totalQty} ticket${totalQty !== 1 ? "s" : ""}`}
      </div>
     </div>
     <div className="sm:hidden flex-1 min-w-0">
      <div className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: theme.accent }}>Step {step}/2</div>
      <div className="text-white text-xs font-bold truncate">{step === 2 ? `$${subtotal.toLocaleString()} · ${totalQty} ticket${totalQty !== 1 ? "s" : ""}` : (evt?.name || theme.title)}</div>
     </div>
     <button disabled={step === 1} onClick={() => setStep(s => Math.max(1, s - 1))} className="px-4 py-3 rounded-full bg-white/8 hover:bg-white/15 text-white text-xs font-black uppercase tracking-[0.22em] disabled:opacity-30 disabled:cursor-not-allowed transition-colors whitespace-nowrap" data-testid="sticky-prev">
      <ArrowLeft className="w-3.5 h-3.5 inline sm:mr-1.5" /><span className="hidden sm:inline">Back</span>
     </button>
     {step < 2 ? (
      <button disabled={!canStep1} onClick={() => setStep(2)} className="px-6 sm:px-8 py-3 rounded-full text-[#0A0A0A] text-xs font-black uppercase tracking-[0.22em] disabled:opacity-40 disabled:cursor-not-allowed transition-all whitespace-nowrap" style={{ background: `linear-gradient(135deg, ${theme.hot} 0%, ${theme.accent} 100%)`, boxShadow: `0 10px 30px -8px ${theme.glow}` }} data-testid="sticky-next">
       Continue <ArrowRight className="w-3.5 h-3.5 inline ml-1.5" />
      </button>
     ) : (
      <button disabled={!canStep2 || submitting} onClick={startCheckout} className="px-6 sm:px-8 py-3 rounded-full text-[#0A0A0A] text-xs font-black uppercase tracking-[0.22em] disabled:opacity-40 disabled:cursor-not-allowed transition-all whitespace-nowrap" style={{ background: `linear-gradient(135deg, ${theme.hot} 0%, ${theme.accent} 100%)`, boxShadow: `0 10px 30px -8px ${theme.glow}` }} data-testid="sticky-checkout">
       {submitting ? "Redirecting…" : <>Pay ${total.toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} <Sparkles className="w-3.5 h-3.5 inline ml-1.5" /></>}
      </button>
     )}
    </div>
   </div>
  </main>
 );
}

function Row({ Icon, label, value, accent }) {
 return (
  <div className="flex items-start gap-3">
   <Icon className="w-5 h-5 mt-0.5 shrink-0" style={{ color: accent }} />
   <div className="flex-1 flex justify-between items-baseline gap-3 flex-wrap">
    <span className="text-white/60 uppercase tracking-widest text-[11px] font-bold">{label}</span>
    <span className="text-white text-right font-bold text-base">{value}</span>
   </div>
  </div>
 );
}
